import { ContextType } from '@/graphql/types-service';
import { PublicEntityKind } from '@/graphql/types-service';
import {
    AdminUserContext,
    contextToUserType,
    LoadedFederalState,
    UserType
} from '@/types';
import { CustomerId, InstitutionId, OrganizationId } from '@qmrs/entityid';
import { defineStore } from 'pinia';

const ROOT_KEY = 'ck';
const ROOT_VAL = 'ckv';
const SUB_KEY = 'sk';
const SUB_VAL = 'skv';

export type SessionContextId =
    CustomerId | OrganizationId | InstitutionId | undefined;

export const useSelectedContextStore = defineStore('selectedContext', () => {
    const rootId = ref<CustomerId | undefined>(undefined);
    const root = ref<AdminUserContext | undefined>(undefined);
    const subId = ref<OrganizationId | InstitutionId | undefined>(undefined);
    const sub = ref<AdminUserContext | undefined>(undefined);
    const type = ref<UserType>(UserType.None);
    const contextId = ref<SessionContextId>(undefined);
    let reloadActor: (() => Promise<void>) | undefined = undefined;
    const initialized = ref(false);

    function storedRootId(): CustomerId | undefined {
        const id = sessionStorage.getItem(ROOT_KEY) ?? null;
        if (id !== null) {
            const v = CustomerId.parse(id);
            if (v.isErr()) {
                sessionStorage.removeItem(ROOT_KEY);
                return;
            }
            return v.unwrap();
        }
    }

    function initRootVal() {
        try {
            const val: AdminUserContext = JSON.parse(
                sessionStorage.getItem(ROOT_VAL) ?? ''
            );
            if (val && val.id) {
                root.value = val;
            }
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (_e) {
            root.value = undefined;
            sessionStorage.removeItem(ROOT_VAL);
        }
    }

    function storedSubId(): OrganizationId | InstitutionId | undefined {
        const id = sessionStorage.getItem(SUB_KEY) ?? null;
        if (id !== null) {
            if (id.startsWith('T')) {
                const v = OrganizationId.parse(id);
                if (v.isErr()) {
                    sessionStorage.removeItem(SUB_KEY);
                    return;
                }
                return v.unwrap();
            } else if (id.startsWith('R')) {
                const v = InstitutionId.parse(id);
                if (v.isErr()) {
                    sessionStorage.removeItem(SUB_KEY);
                    return;
                }
                return v.unwrap();
            }
        }
    }

    function initSubVal() {
        try {
            const val: AdminUserContext = JSON.parse(
                sessionStorage.getItem(SUB_VAL) ?? ''
            );
            if (val && val.id) {
                sub.value = val;
            }
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (_e) {
            sub.value = undefined;
            sessionStorage.removeItem(SUB_VAL);
        }
    }

    function assignContextId() {
        if (subId.value && sub.value) {
            contextId.value = subId.value;
            type.value = contextToUserType(sub.value.ty);
        } else if (rootId.value && root.value) {
            contextId.value = rootId.value;
            type.value = contextToUserType(root.value.ty);
        }
    }

    function init(reloadActorCallback: () => Promise<void>) {
        reloadActor = reloadActorCallback;
        rootId.value = storedRootId();
        if (rootId.value) {
            initRootVal();
        }
        subId.value = storedSubId();
        if (subId.value) {
            initSubVal();
        }
        assignContextId();
        initialized.value = true;
    }

    function setRoot(federalState: LoadedFederalState) {
        root.value = {
            name: federalState.name,
            ty: ContextType.Olb,
            id: federalState.id.toString(),
            kind: PublicEntityKind.Ob
        };
        rootId.value = federalState.id;
        sessionStorage.setItem(ROOT_KEY, federalState.id.toString());
        sessionStorage.setItem(ROOT_VAL, JSON.stringify(root.value));
        assignContextId();
    }

    function setSub(ctx: AdminUserContext) {
        let id;
        if (ctx.id.startsWith('T')) {
            const i = OrganizationId.parse(ctx.id);
            if (i.isOk()) {
                id = i.unwrap();
            }
        } else if (ctx.id.startsWith('R')) {
            const i = InstitutionId.parse(ctx.id);
            if (i.isOk()) {
                id = i.unwrap();
            }
        }
        if (id) {
            sub.value = {
                ...ctx
            };
            subId.value = id;
            sessionStorage.setItem(SUB_KEY, ctx.id);
            sessionStorage.setItem(SUB_VAL, JSON.stringify(sub.value));
        }
        assignContextId();
    }

    function clear() {
        rootId.value = undefined;
        root.value = undefined;
        subId.value = undefined;
        sub.value = undefined;
        type.value = UserType.None;
        contextId.value = undefined;
        sessionStorage.removeItem(ROOT_KEY);
        sessionStorage.removeItem(SUB_KEY);
    }

    function clearSub() {
        subId.value = undefined;
        sub.value = undefined;
        type.value = UserType.Olb;
        contextId.value = rootId.value;
        sessionStorage.removeItem(SUB_KEY);
    }

    const contextType = computed(() => {
        if (sub.value?.ty) {
            return sub.value.ty;
        }
        if (root.value?.ty) {
            return root.value.ty;
        }
        return ContextType.Sys;
    });

    watchDebounced(
        contextId,
        async () => {
            if (initialized.value && reloadActor) {
                await reloadActor();
            }
        },
        {
            maxWait: 100
        }
    );

    return {
        init,
        clear,
        clearSub,
        setRoot,
        setSub,
        rootId,
        root,
        subId,
        contextId,
        sub,
        type,
        contextType
    };
});
